import { useAuth } from "@/_core/hooks/useAuth";
import { ClientLogo } from "@/components/ClientLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, LogOut, Loader2 } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { useLocation } from "wouter";
import { api, type Insumo } from "@/lib/api";
import { formatDisplayText } from "@/lib/formatDisplayText";
import { PAGE_TITLE_STANDALONE_CLASS } from "@/lib/modalStyles";
import { textIncludesSearch } from "@/lib/searchText";
import { PageContentLoader } from "@/components/PageContentLoader";

export default function ConfigEstoque() {
  const { user, logout } = useAuth();
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  
  const isAdmin = user?.role === "admin";
  const allowedTabs = user?.allowed_tabs || null;
  const hasConfigPermission = isAdmin || (Array.isArray(allowedTabs) && allowedTabs.includes("inventario"));
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [estoqueMinimos, setEstoqueMinimos] = useState<Record<number, number>>({});
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [insumos, setInsumos] = useState<Insumo[]>([]);
  const [insumosLoading, setInsumosLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setInsumosLoading(true);
        const lista = await api.listarInsumos();
        setInsumos(lista);
      } catch (e) {
        console.error(e);
        alert("Erro ao carregar insumos");
      } finally {
        setInsumosLoading(false);
      }
    })();
  }, []);

  // Categorias únicas
  const categorias = useMemo(() => {
    const cats = new Set(insumos.map((i) => i.categoria));
    return Array.from(cats).sort();
  }, [insumos]);

  // Filtrar insumos
  const insumosFilterados = useMemo(() => {
    return insumos.filter((insumo) => {
      const matchSearch = textIncludesSearch(insumo.nome, searchTerm);
      const matchCategory = !selectedCategory || insumo.categoria === selectedCategory;
      return matchSearch && matchCategory;
    });
  }, [insumos, searchTerm, selectedCategory]);

  // Agrupar por categoria
  const insumosPorCategoria = useMemo(() => {
    const grouped: Record<string, typeof insumos> = {};
    insumosFilterados.forEach((insumo) => {
      if (!grouped[insumo.categoria]) {
        grouped[insumo.categoria] = [];
      }
      grouped[insumo.categoria].push(insumo);
    });
    return grouped;
  }, [insumosFilterados]);

  const handleEstoqueMinChange = (id: number, value: string) => {
    const num = parseFloat(value) || 0;
    setEstoqueMinimos((prev) => ({
      ...prev,
      [id]: num,
    }));
  };

  const handleSalvarEstoqueMin = async (id: number) => {
    const novoEstoque = estoqueMinimos[id];
    if (novoEstoque === undefined) return;

    setSavingIds((prev) => new Set(Array.from(prev).concat([id])));
    try {
      await api.atualizarEstoqueMinimo(id, novoEstoque);
      setEstoqueMinimos((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
      const atualizados = await api.listarInsumos();
      setInsumos(atualizados);
    } catch (error) {
      alert("Erro ao salvar estoque mínimo");
      console.error(error);
    } finally {
      setSavingIds((prev) => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(id);
        return newSet;
      });
    }
  };

  if (!hasConfigPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Acesso Negado</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Você não tem permissão para acessar esta página.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Header */}
      <div className="bg-accent text-accent-foreground sticky top-0 z-50 shadow-lg">
        <div className="container max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/dashboard")}
              className="text-accent-foreground hover:bg-accent/80 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0 flex-1 flex flex-col justify-center">
              <ClientLogo variant="menu" className="h-8 sm:h-10 w-auto object-contain" />
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-accent-foreground hover:bg-accent/80 flex-shrink-0"
          >
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Sair</span>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-4xl mx-auto px-4 py-6 pb-20">
        {insumosLoading ? (
          <PageContentLoader />
        ) : (
          <>
        <h1 className={`mb-4 ${PAGE_TITLE_STANDALONE_CLASS}`}>Configurar Estoque Mínimo</h1>
        <Card>
          <CardHeader>
            <CardDescription>
              Defina a quantidade mínima de cada insumo para alertas de compra
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Busca */}
            <Input
              placeholder="Buscar insumo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />

            {/* Categorias */}
            <div className="flex flex-wrap gap-2 overflow-x-auto pb-2">
              <Button
                variant={selectedCategory === null ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(null)}
              >
                Todas
              </Button>
              {categorias.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </Button>
              ))}
            </div>

            {/* Insumos */}
              <div className="space-y-6">
                {Object.entries(insumosPorCategoria).map(([categoria, items]) => (
                  <div key={categoria} className="space-y-3">
                    <h3 className="font-semibold text-accent text-lg">{formatDisplayText(categoria)}</h3>
                    <div className="space-y-3">
                      {items.map((insumo) => {
                        const hasChanges = estoqueMinimos[insumo.id] !== undefined;
                        const isSaving = savingIds.has(insumo.id);
                        const currentMin = estoqueMinimos[insumo.id] ?? insumo.estoqueMinimo;

                        return (
                          <div
                            key={insumo.id}
                            className="p-4 rounded-lg border-2 border-gray-200 bg-card hover:border-accent/50 transition-all"
                          >
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex-1">
                                <h4 className="font-medium text-foreground">{formatDisplayText(insumo.nome)}</h4>
                                <p className="text-sm text-muted-foreground">
                                  Unidade: {insumo.unidade}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                              <div className="flex-1">
                                <label className="text-sm text-muted-foreground">
                                  Estoque Mínimo
                                </label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  value={currentMin}
                                  onChange={(e) => handleEstoqueMinChange(insumo.id, e.target.value)}
                                  className="mt-1 text-lg h-10 w-full"
                                />
                              </div>
                              {hasChanges && (
                                <Button
                                  onClick={() => handleSalvarEstoqueMin(insumo.id)}
                                  disabled={isSaving}
                                  className="mt-6 sm:mt-0 bg-accent hover:bg-accent/90 w-full sm:w-auto"
                                  size="sm"
                                >
                                  {isSaving ? (
                                    <>
                                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                      Salvando
                                    </>
                                  ) : (
                                    "Salvar"
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </div>
  );
}
